import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import type { SelectOption } from './Select';

export interface SearchSelectProps {
  label?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  fullWidth?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  id?: string;
  disabled?: boolean;
}

export default function SearchSelect({
  label,
  options,
  value,
  onChange,
  onBlur,
  error,
  fullWidth = true,
  placeholder = 'Choisir…',
  searchPlaceholder = 'Rechercher…',
  id,
  disabled = false,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  const filteredOptions = query.trim()
    ? options.filter((o) =>
        o.label.toLowerCase().includes(query.toLowerCase().trim())
      )
    : options;

  useEffect(() => {
    if (!open) {
      setQuery('');
      onBlurRef.current?.();
      return;
    }
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const selectId = id ?? label?.toLowerCase().replace(/\s/g, '-');

  return (
    <div ref={containerRef} className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label
          htmlFor={selectId}
          className="mb-1.5 block text-sm font-medium text-slate-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id={selectId}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          onBlur={() => {}}
          className={`pro-input flex w-full items-center justify-between gap-2 text-left ${
            fullWidth ? 'w-full' : ''
          } ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              : ''
          } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={label}
        >
          <span className={value ? 'text-slate-800' : 'text-slate-500'}>
            {displayLabel}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>

        {open && (
          <div
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
            role="listbox"
          >
            <div className="border-b border-slate-200 p-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="pro-input w-full pl-9 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setOpen(false);
                  }}
                />
              </div>
            </div>
            <ul className="max-h-48 overflow-y-auto py-1">
              {filteredOptions.length === 0 ? (
                <li className="px-3 py-2 text-sm text-slate-500">
                  Aucun résultat
                </li>
              ) : (
                filteredOptions.map((opt) => (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={opt.value === value}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                      opt.value === value
                        ? 'bg-teal-50 text-teal-800'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {opt.label}
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
